// Pure helpers shared between v3 Dashboard / MandatesList / MandateDetail.
// They (a) coerce strings-from-PG-numeric into real numbers, (b) format ZAR
// values to compact "R 1.8bn" style, and (c) derive the design's "stage" /
// "code" / "id" semantics from the Mandate fields that *do* exist today.

import type { Mandate } from '../../interfaces/client/mandate.interface';

/** Rails returns PG `numeric` columns as strings — coerce defensively. */
export const num = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** "R 1.8bn" / "R 940m" / "R 25.3m" / "R 412k".  Returns "—" when missing. */
export const formatZar = (value: unknown): string => {
  const v = num(value);
  if (v === 0) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `R ${(v / 1_000_000_000).toFixed(1)}bn`;
  if (abs >= 1_000_000) return `R ${(v / 1_000_000).toFixed(0)}m`;
  if (abs >= 1_000) return `R ${(v / 1_000).toFixed(0)}k`;
  return `R ${v}`;
};

/** Cheap deterministic hash → small integer. */
export const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

/**
 * Map a Mandate to one of the design's named stage chips.
 *
 * Reads the real `stage` enum column when the back-end has populated it
 * (after `rails v3:backfill_mandates`).  Falls back to deriving from `status`
 * + `transaction_type` for any rows that pre-date the column or any callers
 * still on the legacy API.
 */
const STAGE_ENUM_LABELS: Record<string, string> = {
  execution: 'Execution',
  due_diligence: 'Due Diligence',
  pre_listing: 'Pre-listing',
  implementation: 'Implementation',
  closing: 'Closing',
  reporting: 'Reporting',
  structuring: 'Structuring',
  field_work: 'Field Work',
  closed: 'Closed'
};

const STAGE_FREETEXT_MAP: Record<string, string> = {
  execution: 'Execution',
  'due diligence': 'Due Diligence',
  dd: 'Due Diligence',
  'pre-listing': 'Pre-listing',
  prelisting: 'Pre-listing',
  listing: 'Pre-listing',
  implementation: 'Implementation',
  closing: 'Closing',
  reporting: 'Reporting',
  structuring: 'Structuring',
  'field work': 'Field Work',
  closed: 'Closed'
};

export const deriveStage = (m: Mandate): string => {
  // Real column wins.
  if (m.stage && STAGE_ENUM_LABELS[m.stage]) return STAGE_ENUM_LABELS[m.stage];
  // Legacy fallback.
  const status = (m.status || '').toString().toLowerCase().trim();
  if (status === 'completed' || status === 'closed') return 'Closed';
  const type = (m.transaction_type || '').toString().toLowerCase().trim();
  return STAGE_FREETEXT_MAP[type] || 'Execution';
};

/**
 * Real progress (0..100), or undefined when the column is empty.  This used to
 * fall back to `40 + hash(id) % 56`, rendering a stable, authoritative-looking
 * bar for a mandate nobody had ever set progress on.
 */
export const deriveProgress = (m: Mandate): number | undefined => {
  if (typeof m.progress === 'number' && Number.isFinite(m.progress)) return m.progress;
  return undefined;
};

/** Real lead full-name when present, else `fallback` (or undefined). */
export const deriveLead = (m: Mandate, fallback?: string): string | undefined => {
  if (m.lead && m.lead.trim().length > 0) return m.lead;
  return fallback;
};

/** Project codename in the design.  Uses the Mandate `name`. */
export const deriveCode = (m: Mandate): string => m.name || `Mandate ${m.id}`;
/** Formatted id like "M-0014". */
export const deriveId = (m: Mandate): string => `M-${String(m.id).padStart(4, '0')}`;

export const initialsFromName = (name?: string): string => {
  if (!name) return '—';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
};

/** YYYY-MM-DD from a date-ish input.  Returns "—" if unparseable. */
export const formatDate = (input: string | Date | undefined | null): string => {
  if (!input) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Days between two dates (positive = b is later than a).  Returns null if invalid. */
export const daysBetween = (
  a: string | Date | undefined | null,
  b: string | Date | undefined | null
): number | null => {
  if (!a || !b) return null;
  const aa = a instanceof Date ? a : new Date(a);
  const bb = b instanceof Date ? b : new Date(b);
  if (Number.isNaN(aa.getTime()) || Number.isNaN(bb.getTime())) return null;
  return Math.round((bb.getTime() - aa.getTime()) / (1000 * 60 * 60 * 24));
};

/** Year part of an ISO date / Date / null.  Falls back to "—". */
export const yearOf = (input: string | Date | undefined | null): string | number => {
  if (!input) return '—';
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? '—' : d.getFullYear();
};

/** Real tier (capitalised for UI) when present, else deterministic fallback. */
export const deriveTier = (client: {
  id?: string | number;
  name?: string;
  tier?: string;
}): 'Strategic' | 'Growth' | 'Emerging' | undefined => {
  const t = (client.tier || '').toLowerCase();
  if (t === 'strategic') return 'Strategic';
  if (t === 'growth') return 'Growth';
  if (t === 'emerging') return 'Emerging';
  return undefined;
};

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** "Nov 2025" / "Mar 2024".  Returns "—" if input is unparseable. */
export const formatMonthYear = (input: string | Date | undefined | null): string => {
  if (!input) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Capitalised UI labels for the design's four employee teams.  The DB now
 * stores lowercase enum values; both ends of the swap live here.
 */
export type EmployeeTeam = 'Advisory' | 'Sponsors' | 'Compliance' | 'Operations';

const EMPLOYEE_TEAM_LABEL: Record<string, EmployeeTeam> = {
  advisory: 'Advisory',
  sponsors: 'Sponsors',
  compliance: 'Compliance',
  operations: 'Operations'
};

/**
 * Real team when the back-end has populated `users.team` (via
 * `rails v3:backfill_employees`); otherwise the same deterministic stub the
 * v3 frontend used pre-migration.
 */
export const deriveTeam = (employee: {
  id?: string | number;
  role?: string;
  erole?: string;
  team?: string;
}): EmployeeTeam | undefined => {
  const t = (employee.team || '').toLowerCase();
  if (EMPLOYEE_TEAM_LABEL[t]) return EMPLOYEE_TEAM_LABEL[t];
  // Inferring from the person's real role/job title is grounded in stored
  // data, so it stays; the hash-bucket fallback does not.
  return inferTeamFromRole(employee.role, employee.erole);
};

/** Team inferred from the person's real role / job title. */
export const inferTeamFromRole = (role?: string, erole?: string): EmployeeTeam | undefined => {
  if ((role || '').toLowerCase() === 'human_resources') return 'Operations';
  const t = (erole || '').toLowerCase();
  if (t.includes('compliance')) return 'Compliance';
  if (t.includes('sponsor')) return 'Sponsors';
  if (t.includes('operations') || t.includes('admin')) return 'Operations';
  return undefined;
};

/**
 * Real office, or undefined.  This used to hash the id into one of Cape Town /
 * Johannesburg / Sandton, showing a named colleague in a random office.
 */
export const deriveOffice = (employee: {
  id?: string | number;
  office?: string;
}): string | undefined => {
  if (employee.office && employee.office.trim().length > 0) return employee.office;
  return undefined;
};

/**
 * Real deal type when the back-end has populated `tombstones.deal_type`,
 * otherwise derive from the free-text category like the old stub.
 */
export const deriveTombstoneType = (t: { deal_type?: string; category?: string }): string => {
  if (t.deal_type && t.deal_type.trim().length > 0) return t.deal_type;
  return stubTombstoneType(t.category);
};

/**
 * Real BSM role, or undefined.  The old fallback picked from an invented list
 * ("Sell-side advisor", "JSE Sponsor", …) by id hash, putting a specific and
 * wrong mandate role against real closed transactions.
 */
export const deriveTombstoneRole = (t: {
  id?: string | number;
  bsm_role?: string;
}): string | undefined => {
  if (t.bsm_role && t.bsm_role.trim().length > 0) return t.bsm_role;
  return undefined;
};

/** Maps a Tombstone category to the design's broad type chip. */
export const stubTombstoneType = (category?: string): string => {
  if (!category) return 'Transaction';
  const c = category.toLowerCase();
  if (
    c.includes('m&a') ||
    c.includes('merger') ||
    c.includes('acquisition') ||
    c.includes('disposal')
  )
    return 'M&A';
  if (c.includes('listing') || c.includes('ipo') || c.includes('jse')) return 'Listing';
  if (c.includes('capital') || c.includes('raise') || c.includes('debt') || c.includes('bond'))
    return 'Capital Raise';
  if (c.includes('restructur')) return 'Restructuring';
  if (c.includes('valuation')) return 'Valuation';
  if (c.includes('bee')) return 'BEE';
  return category;
};

/** Pull the file extension from a filename, uppercased.  Returns "" if none. */
export const fileExtension = (filename?: string): string => {
  if (!filename) return '';
  const idx = filename.lastIndexOf('.');
  if (idx < 0) return '';
  return filename.slice(idx + 1).toUpperCase();
};

/**
 * Format file bytes to a human-readable string.  We don't have the byte size
 * on the back-end yet — accept either a number (bytes) or undefined and
 * return "—" in that case.
 */
export const formatBytes = (bytes: number | undefined): string => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/**
 * Real access scope, or undefined.  The old fallback hashed the id into one of
 * five invented levels — the most misleading stub, since it reads as a
 * security control.
 */
export const deriveAccess = (d: {
  id?: string | number;
  access_scope?: string;
}): string | undefined => {
  if (d.access_scope && d.access_scope.trim().length > 0) return d.access_scope;
  return undefined;
};
